import React, { useState } from 'react'
import styled from 'styled-components'
import NorthIcon from '@mui/icons-material/North'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { ChangeVariant } from './TitleChange'
import { Flex as FlexIcon, Micromobility as MicromobilityIcon } from 'core-system/Icons/Sidebar'
import { Car as CarIcon } from 'core-system/Icons/modeTypes'
import CreditCardIcon from 'core-system/Icons/Accounts/CreditCard'
import InsightsIcon from 'core-system/Icons/Sidebar/Insights'
import OneTimePurchaseIcon from 'core-system/Icons/Micromobility/OneTimePurchase'
import CouponIcon from 'core-system/Icons/Programs/Coupon'
import {
  DashboardPeriod,
  DashboardProgramCard,
  DashboardProgramSiteEnrollment,
} from '../dashboardTypes'

const VISIBLE_SITES = 3

const PERIOD_BADGE_LABEL: Record<DashboardPeriod, string> = {
  7: 'this 7d',
  30: 'this 30d',
  90: 'this 90d',
  all: 'all time',
}

/** Left accent by primary program type (multi-type launches use priority order). */
const PROGRAM_TYPE_ACCENT: Record<string, string> = {
  TRANSIT_PASS: palette.accent.teal500,
  FLEX: palette.secondary.purple500,
  MICROMOBILITY: palette.accent.blue500,
  GRH: palette.warning.yellow600,
  OTP: palette.primary.pink500,
  COUPON: palette.success.green500,
  REPORTED_USAGE: palette.accent.blue600,
}

const PROGRAM_TYPE_PRIORITY = [
  'TRANSIT_PASS',
  'FLEX',
  'MICROMOBILITY',
  'GRH',
  'OTP',
  'COUPON',
  'REPORTED_USAGE',
] as const

export const getProgramCardAccentColor = (types: string[]): string => {
  for (const type of PROGRAM_TYPE_PRIORITY) {
    if (types.includes(type)) {
      return PROGRAM_TYPE_ACCENT[type]
    }
  }
  return palette.grey.grey500
}

export const PROGRAM_TYPE_ICON: Record<
  string,
  React.ComponentType<{ width?: number; height?: number; color?: string }>
> = {
  TRANSIT_PASS: CreditCardIcon,
  FLEX: FlexIcon,
  MICROMOBILITY: MicromobilityIcon,
  GRH: CarIcon,
  OTP: OneTimePurchaseIcon,
  COUPON: CouponIcon,
  REPORTED_USAGE: InsightsIcon,
}

export const buildProgramCardSubtitle = (
  card: Pick<DashboardProgramCard, 'transitAgencies' | 'scopes'>
): string => {
  const regions = [
    ...new Set(
      card.transitAgencies
        .map((agency) => agency.region?.trim())
        .filter(Boolean)
    ),
  ]
  const scopeNames = card.scopes.map((scope) => scope.name).filter(Boolean)
  return [...regions, ...scopeNames].join(' · ')
}

const Card = styled.div<{ $clickable?: boolean }>`
  width: 100%;
  min-height: 14rem;
  background: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  box-shadow: ${(props) => props.theme.dropShadows.normal};
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;
  box-sizing: border-box;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: border-color 0.15s ease;

  ${(props) =>
    props.$clickable
      ? `&:hover { border-color: ${palette.grey.grey300}; }`
      : ''}

  &:focus-visible {
    outline: 2px solid ${palette.accent.teal500};
    outline-offset: 2px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
`

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
`

const Badge = styled.div<{ variant: ChangeVariant }>`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.25rem 0.625rem;
  border-radius: 1rem;
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 500;
  background-color: ${({ variant }) => {
    if (variant === 'positive') {
      return palette.success.green200
    }
    if (variant === 'negative') {
      return palette.error.red200
    }
    return palette.grey.grey200
  }};
  color: ${({ variant }) => {
    if (variant === 'positive') {
      return palette.success.green800
    }
    if (variant === 'negative') {
      return palette.error.red800
    }
    return palette.grey.grey800
  }};
`

const IconBadge = styled.div<{ $color: string }>`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  background: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const Metrics = styled.div`
  display: flex;
  gap: 2rem;
`

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

const Sites = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-top: auto;
`

const SiteRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 9.5rem) minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
`

const SiteName = styled.div`
  min-width: 0;
  overflow: hidden;
`

const MoreSites = styled.span`
  width: fit-content;
  color: ${palette.text.secondary};
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const BarTrack = styled.div`
  height: 0.5rem;
  width: 100%;
  border-radius: 999px;
  background: ${palette.grey.grey100};
  overflow: hidden;
`

const BarFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  width: ${(props) => props.$width}%;
  border-radius: 999px;
  background: ${(props) => props.$color};
`

const formatCount = (value: number) => value.toLocaleString('en-US')

const formatExactMoney = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`

const formatScaled = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1)

export const formatCompactMoney = (cents: number) => {
  const dollars = Math.round(cents / 100)
  const sign = dollars < 0 ? '-' : ''
  const abs = Math.abs(dollars)

  if (abs >= 1_000_000) {
    return `${sign}$${formatScaled(Math.round((abs / 1_000_000) * 10) / 10)}m`
  }
  if (abs >= 1_000) {
    const scaled = Math.round((abs / 1_000) * 10) / 10
    if (scaled >= 1000) return `${sign}$1m`
    return `${sign}$${formatScaled(scaled)}k`
  }
  return `${sign}$${abs.toLocaleString('en-US')}`
}

export const primaryProgramType = (types: string[]) =>
  PROGRAM_TYPE_PRIORITY.find((type) => types.includes(type)) ?? types[0]

interface ProgramCardProps {
  card: DashboardProgramCard
  period?: DashboardPeriod
  onClick?: (card: DashboardProgramCard) => void
}

const ProgramCard = ({ card, period = 'all', onClick }: ProgramCardProps) => {
  const accentColor = getProgramCardAccentColor(card.types)
  const type = primaryProgramType(card.types)
  const Icon = type ? PROGRAM_TYPE_ICON[type] : undefined
  const periodMetrics = card.byPeriod?.[period]
  const spendCents = periodMetrics?.moneySpentCents ?? 0
  const delta = periodMetrics?.enrolledDelta ?? 0
  const periodLabel = PERIOD_BADGE_LABEL[period]
  const badgeVariant: ChangeVariant =
    delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral'
  const badgeLabel =
    delta > 0
      ? `+${delta} ${periodLabel}`
      : delta < 0
        ? `${delta} ${periodLabel}`
        : `0 ${periodLabel}`
  const [sitesExpanded, setSitesExpanded] = useState(false)
  const sites = card.siteEnrollments ?? []
  const hiddenSites = Math.max(sites.length - VISIBLE_SITES, 0)
  const visibleSites = sitesExpanded ? sites : sites.slice(0, VISIBLE_SITES)
  const toggleSites = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setSitesExpanded((open) => !open)
  }
  const maxEnrolled = Math.max(...sites.map((site) => site.enrolled), 1)

  return (
    <Card
      as={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      $clickable={Boolean(onClick)}
      data-cy='dashboard-program-card'
      aria-label={
        onClick ? `View ${card.superProgramName} program details` : undefined
      }
      onClick={onClick ? () => onClick(card) : undefined}
    >
      <Header>
        <TitleGroup>
          <IconBadge $color={accentColor}>
            {Icon ? <Icon width={18} height={18} color={palette.white} /> : null}
          </IconBadge>
          <Text variant='action2' fontWeight={600} noWrap>
            {card.superProgramName}
          </Text>
        </TitleGroup>
        {period === 'all' ? null : (
          <Badge variant={badgeVariant} data-cy='program-card-delta'>
            <NorthIcon
              sx={{
                fontSize: '0.75rem',
                transform:
                  badgeVariant === 'negative'
                    ? 'rotate(180deg)'
                    : badgeVariant === 'neutral'
                      ? 'rotate(90deg)'
                      : 'none',
              }}
            />
            {badgeLabel}
          </Badge>
        )}
      </Header>

      <Metrics>
        <Metric>
          <Text variant='h2' fontWeight={600}>
            {formatCount(card.enrolled)}
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            Enrolled
          </Text>
        </Metric>
        <Metric>
          <Text variant='h2' fontWeight={600} dataCy='program-card-sites-active'>
            {card.sitesActive}/{card.sitesTotal}
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            Sites Active
          </Text>
        </Metric>
        <Metric>
          <Text
            variant='h2'
            fontWeight={600}
            dataCy='program-card-spend'
            title={formatExactMoney(spendCents)}
          >
            {formatCompactMoney(spendCents)}
          </Text>
          <Text variant='body1' color={palette.text.secondary}>
            {card.types.includes('TRANSIT_PASS') ? 'Topups' : 'Spend'}
          </Text>
        </Metric>
      </Metrics>

      {visibleSites.length > 0 ? (
        <Sites>
          {visibleSites.map((site: DashboardProgramSiteEnrollment) => (
            <SiteRow key={site.id}>
              <SiteName title={site.name}>
                <Text variant='body1' color={palette.text.secondary} hideOverflow>
                  {site.name}
                </Text>
              </SiteName>
              <BarTrack>
                <BarFill
                  $width={Math.round((site.enrolled / maxEnrolled) * 100)}
                  $color={accentColor}
                />
              </BarTrack>
              <Text variant='body1' fontWeight={600}>
                {formatCount(site.enrolled)}
              </Text>
            </SiteRow>
          ))}
          {hiddenSites > 0 ? (
            <MoreSites
              role='button'
              tabIndex={0}
              onClick={toggleSites}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  toggleSites(event)
                }
              }}
            >
              {sitesExpanded
                ? 'Show less'
                : `+${hiddenSites} more ${hiddenSites === 1 ? 'site' : 'sites'}`}
            </MoreSites>
          ) : null}
        </Sites>
      ) : null}
    </Card>
  )
}

export default React.memo(ProgramCard)
